/**
 * SERVIÇO DE AUTENTICAÇÃO PADELMASTER
 * 
 * Este serviço gerencia todas as operações de autenticação:
 * - Login tradicional
 * - Registro de usuários
 * - Login com Google
 * - Validação de tokens
 * - Gestão de sessões
 */

import {
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  GoogleAuthRequest,
  CSRFResponse,
  UserData,
  PasswordResetResponse,
  PasswordResetConfirmResponse
} from '../types';
import { tryMultipleEndpoints, generateEndpointVariations } from '../utils/apiUtils';
import { API_CONFIG, API_ENDPOINTS } from '../config/api';
import axios from 'axios';
import { refreshSession } from '@/security/refreshSession';
import { lerAccessToken, lerRefreshToken, limparTokens, guardarTokens } from '@/security/tokens';

/**
 * 🔐 Login Unificado V2 - Usa novo endpoint /api/auth/login/
 *
 * Aceita email OU username
 * Endpoint: POST /api/auth/login/
 * Rate limit: 5 tentativas/minuto
 */
export async function loginWithEmailPassword(
  email: string,
  password: string
): Promise<LoginResponse | null> {
  try {
    console.log('🔑 [LOGIN V2] Iniciando login com endpoint unificado...');
    console.log('🔑 [LOGIN V2] Credential:', email);

    const loginUrl = `${API_CONFIG.BASE_URL}/api/auth/login/`;

    console.log('🔑 [LOGIN V2] Fazendo requisição para:', loginUrl);

    const response = await axios.post<LoginResponse>(loginUrl, {
      credential: email.trim(),  // Não alterar a palavra-passe.
      password: password
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      timeout: 10000,
      withCredentials: true,  // Permitir cookies HttpOnly
    });

    console.log('✅ [LOGIN V2] Resposta recebida:', {
      status: response.status,
      hasData: !!response.data
    });

    if (response.data && response.data.status === 'success') {
      console.log('✅ [LOGIN V2] Login bem-sucedido');

      // Salvar tokens no localStorage
      guardarTokens(response.data.access, response.data.refresh);

      // Salvar timestamp do login para refresh automático
      localStorage.setItem('loginTimestamp', Date.now().toString());

      return response.data;
    }

    // Tratar erros do backend
    if (response.data && response.data.status === 'error') {
      const errorMessage = response.data.message || 'Erro desconhecido';
      console.error('❌ [LOGIN V2] Erro do backend:', errorMessage);
      throw new Error(errorMessage);
    }

    return null;
  } catch (error) {
    console.error('❌ [LOGIN V2] Erro no login:', error);

    if (axios.isAxiosError(error)) {
      const errorData = error.response?.data;
      console.error('❌ [LOGIN V2] Detalhes do erro:', {
        status: error.response?.status,
        code: errorData?.code,
        message: errorData?.message
      });

      // Re-throw com mensagem apropriada
      if (errorData?.message) {
        throw new Error(errorData.message);
      }
      if (error.response?.status === 429) {
        throw new Error('Demasiadas tentativas de entrada. Aguarda um minuto e tenta novamente.');
      }
      throw new Error(error.response
        ? 'Não foi possível entrar. Tenta novamente dentro de instantes.'
        : 'Não foi possível contactar o servidor. Verifica a ligação à internet e tenta novamente.');
    }

    throw error;
  }
}

/**
 * 📝 Registra um novo usuário
 */
export async function registerUser(
  userData: RegisterRequest
): Promise<RegisterResponse | null> {
  try {
    console.log('📝 Iniciando registro de usuário...');
    
    const endpoints = generateEndpointVariations('register');
    
    const response = await tryMultipleEndpoints<RegisterResponse>(
      'register',
      endpoints,
      { 
        data: userData,
        timeout: 15000 
      },
      'POST'
    );
    
    if (response) {
      console.log('✅ Usuário registrado com sucesso');
    }
    
    return response;
  } catch (error) {
    console.error('❌ Erro no registro:', error);
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as {
        message?: string;
        detail?: string;
        error?: string;
        email?: string[];
      } | undefined;
      const backendMessage = data?.message || data?.detail || data?.error || data?.email?.[0];
      if (backendMessage) throw new Error(backendMessage);
      if (error.response?.status === 409) throw new Error('Este email já está registado. Tenta entrar.');
      if (error.response?.status === 429) throw new Error('Demasiadas tentativas. Aguarda um pouco e tenta novamente.');
      if (!error.response) throw new Error('Não foi possível contactar o servidor. Verifica a ligação e tenta novamente.');
    }
    throw error;
  }
}

/**
 * 🌐 Autenticação com Google V2 - Com Account Linking
 *
 * Endpoint: POST /api/auth/google/
 * Features:
 * - Account Linking automático (se email já existe, associa Google)
 * - HttpOnly cookies para tokens
 * - Detecta se é utilizador novo ou existente
 */
export async function loginWithGoogle(
  googleUserData: GoogleAuthRequest
): Promise<LoginResponse | null> {
  try {
    console.log('🌐 [GOOGLE V2] Iniciando autenticação com Google...');
    console.log('🌐 [GOOGLE V2] Email:', googleUserData.email);

    const googleUrl = `${API_CONFIG.BASE_URL}/api/auth/google/`;

    const response = await axios.post<LoginResponse>(googleUrl, {
      google_id: googleUserData.id || googleUserData.google_id,
      email: googleUserData.email,
      name: googleUserData.name || googleUserData.displayName,
      picture: googleUserData.picture || googleUserData.photoURL
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      timeout: 15000,
      withCredentials: true,  // Permitir cookies HttpOnly
    });

    console.log('✅ [GOOGLE V2] Resposta recebida:', {
      status: response.status,
      hasData: !!response.data
    });

    if (response.data && response.data.status === 'success') {
      console.log('✅ [GOOGLE V2] Login Google bem-sucedido');
      console.log('🔗 [GOOGLE V2] Account Linking:', response.data.account_linked);
      console.log('🆕 [GOOGLE V2] Novo utilizador:', response.data.is_new_user);
      console.log('🔑 [GOOGLE V2] Login method:', response.data.login_method);

      // Salvar tokens no localStorage (compatibilidade)
      guardarTokens(response.data.access, response.data.refresh);

      // Salvar timestamp do login
      localStorage.setItem('loginTimestamp', Date.now().toString());

      return response.data;
    }

    // Tratar erros do backend
    if (response.data && response.data.status === 'error') {
      const errorMessage = response.data.message || 'Erro desconhecido';
      console.error('❌ [GOOGLE V2] Erro do backend:', errorMessage);
      throw new Error(errorMessage);
    }

    return null;
  } catch (error) {
    console.error('❌ [GOOGLE V2] Erro no login Google:', error);

    if (axios.isAxiosError(error)) {
      const errorData = error.response?.data;
      console.error('❌ [GOOGLE V2] Detalhes do erro:', {
        status: error.response?.status,
        code: errorData?.code,
        message: errorData?.message
      });

      // Re-throw com mensagem apropriada
      if (errorData?.message) {
        throw new Error(errorData.message);
      }
    }

    throw error;
  }
}

/**
 * ✅ Valida um token JWT e retorna dados do usuário
 */
export async function validateToken(token: string): Promise<UserData | null> {
  try {
    console.log('✅ [AUTH-SERVICE] Validando token...');
    
    // Django exige a barra final em POST; evitar variantes e cache de URLs antigas.
    const { data: response } = await axios.post<{ user: UserData }>(
      API_ENDPOINTS.AUTH.VALIDATE_TOKEN,
      { token },
      {
        timeout: 10000,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response?.user) {
      console.log('✅ [AUTH-SERVICE] Token válido - usuário encontrado:', response.user.email);
      return response.user;
    } else {
      console.log('❌ [AUTH-SERVICE] Token inválido - resposta sem dados de usuário');
      return null;
    }
  } catch (error) {
    console.error('❌ [AUTH-SERVICE] Erro na validação do token:', error);
    
    // Log mais detalhado do erro
    if (error instanceof Error) {
      console.error('❌ [AUTH-SERVICE] Detalhes do erro:', {
        message: error.message,
        name: error.name
      });
    }
    
    return null;
  }
}

/**
 * 🔒 Obtém token CSRF
 */
export async function getCSRFToken(): Promise<string | null> {
  try {
    const endpoints = generateEndpointVariations('get-csrf-token');
    
    const response = await tryMultipleEndpoints<CSRFResponse>(
      'csrf-token',
      endpoints,
      { timeout: 5000 },
      'GET'
    );
    
    return response?.csrfToken || null;
  } catch (error) {
    console.error('❌ Erro ao obter token CSRF:', error);
    return null;
  }
}

/**
 * 🚪 Faz logout completo (limpa tokens e sessão)
 */
export async function logout(): Promise<void> {
  console.log('🚪 [AUTH-SERVICE] Iniciando logout completo...');
  
  // Limpar tokens de TODAS as convenções de nome. Antes só apagava
  // 'accessToken'/'refreshToken' e deixava 'padelmaster_access_token' para
  // trás — a app continuava a encontrar sessão depois do logout.
  const access = lerAccessToken();
  const refresh = lerRefreshToken();
  limparTokens();
  localStorage.removeItem('sessionRestored');
  
  // Limpar outros dados relacionados à sessão
  localStorage.removeItem('userProfile');
  localStorage.removeItem('lastLoginTime');
  
  console.log('✅ [AUTH-SERVICE] Logout completo realizado');
  
  // Limpar também os cookies HttpOnly do backend. O fetch é direto para não
  // disparar uma renovação de sessão enquanto estamos a terminar a sessão.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    await Promise.allSettled([
      fetch(`${API_CONFIG.BASE_URL}/api/auth/logout/`, {
        method: 'POST', credentials: 'include', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', ...(access ? { Authorization: `Bearer ${access}` } : {}) },
        body: JSON.stringify({ refresh }),
      }),
      'caches' in window ? caches.delete('padel-api') : Promise.resolve(false),
    ]);
  } finally {
    clearTimeout(timer);
    window.location.href = '/login';
  }
}

// Estado para evitar logs repetitivos
let lastAuthState: boolean | null = null;
let lastAuthLog = 0;
const AUTH_LOG_COOLDOWN = 5000; // 5 segundos

/**
 * 🔍 Verifica se o usuário está autenticado (versão melhorada)
 */
export function isAuthenticated(): boolean {
  const token = lerAccessToken();
  const hasToken = !!token;

  // Log apenas se mudou o estado ou passou o cooldown
  const now = Date.now();
  if (lastAuthState !== hasToken || (now - lastAuthLog) > AUTH_LOG_COOLDOWN) {
    if (hasToken) {
      console.log('🔐 [AUTH-SERVICE] Token encontrado - usuário autenticado');
    } else {
      console.log('🔐 [AUTH-SERVICE] Nenhum token encontrado - usuário não autenticado');
    }
    lastAuthState = hasToken;
    lastAuthLog = now;
  }

  return hasToken;
}

/**
 * 🎫 Obtém o token de acesso atual
 */
export function getAccessToken(): string | null {
  return lerAccessToken();
}

/**
 * 🔄 Obtém o token de refresh atual
 */
export function getRefreshToken(): string | null {
  return lerRefreshToken();
}

/**
 * 🔄 Atualiza o token de acesso automaticamente
 *
 * Endpoint: POST /api/auth/refresh/
 * Rate limit: 10 tentativas/minuto
 */
export async function refreshAccessToken(): Promise<string | null> {
  return (await refreshSession()).access;
}

/**
 * ⏰ Verifica se o token precisa ser renovado e renova automaticamente
 *
 * Usa o `exp` real do JWT (o access token dura 1 dia no backend): renova
 * quando faltar menos de 6h ou já tiver expirado. O refresh token dura 90
 * dias e roda a cada renovação — quem abrir a app pelo menos uma vez a cada
 * 90 dias nunca volta a ver o login.
 */
export async function checkAndRefreshToken(): Promise<boolean> {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return false;
  }

  let expiresAt = 0;
  try {
    expiresAt = JSON.parse(atob(accessToken.split('.')[1])).exp * 1000;
  } catch {
    // token ilegível — tentar renovar na mesma
  }

  if (Date.now() < expiresAt - 6 * 60 * 60 * 1000) {
    return true; // ainda válido por mais de 6h
  }

  console.log('⏰ [AUTO-REFRESH] Token expirado ou perto de expirar, renovando...');
  const newToken = await refreshAccessToken();
  return !!newToken;
}

/**
 * 📧 Solicita reset de senha por email
 */
export async function requestPasswordReset(
  email: string
): Promise<PasswordResetResponse | null> {
  try {
    console.log('📧 Solicitando reset de senha...');

    // Rotas reais do backend (authentication.urls está montado sob /backend/)
    const endpoints = [
      `${API_CONFIG.BASE_URL}/backend/padel/password-reset/request/`,
    ];

    const response = await tryMultipleEndpoints<PasswordResetResponse>(
      'password-reset-request',
      endpoints,
      {
        data: { email },
        timeout: 15000
      },
      'POST'
    );
    
    if (response) {
      console.log('✅ Solicitação de reset enviada com sucesso');
    }
    
    return response;
  } catch (error) {
    console.error('❌ Erro ao solicitar reset de senha:', error);
    return null;
  }
}

/**
 * 🔐 Confirma reset de senha com token
 */
export async function confirmPasswordReset(
  token: string,
  newPassword: string
): Promise<PasswordResetConfirmResponse | null> {
  try {
    console.log('🔐 Confirmando reset de senha...');

    // Rotas reais do backend (authentication.urls está montado sob /backend/)
    const endpoints = [
      `${API_CONFIG.BASE_URL}/backend/padel/password-reset/confirm/`,
    ];

    const response = await tryMultipleEndpoints<PasswordResetConfirmResponse>(
      'password-reset-confirm',
      endpoints,
      {
        // O backend (reset_password_confirm) lê 'new_password'
        data: { token, new_password: newPassword },
        timeout: 15000
      },
      'POST'
    );

    if (response) {
      console.log('✅ Reset de senha confirmado com sucesso');
    }

    return response;
  } catch (error) {
    console.error('❌ Erro ao confirmar reset de senha:', error);
    return null;
  }
}


// ================================================================
// 🔗 ACCOUNT LINKING - Gestão de Métodos de Login
// ================================================================

export interface LoginMethodsResponse {
  status: string;
  login_method: 'password' | 'google' | 'both';
  has_password: boolean;
  has_google: boolean;
  google_id?: string;
  can_unlink_google: boolean;
  email: string;
}

export interface SetPasswordResponse {
  status: string;
  message: string;
  login_method: string;
  access?: string;
  refresh?: string;
}

export interface LinkGoogleResponse {
  status: string;
  message: string;
  login_method: string;
  has_google: boolean;
  has_password: boolean;
}

/**
 * 🔗 Obtém os métodos de login do utilizador atual
 */
export async function getLoginMethods(): Promise<LoginMethodsResponse | null> {
  try {
    console.log('🔗 [AUTH-SERVICE] Obtendo métodos de login...');

    const token = getAccessToken();
    if (!token) {
      console.error('❌ [AUTH-SERVICE] Sem token de acesso');
      return null;
    }

    const response = await axios.get<LoginMethodsResponse>(
      `${API_CONFIG.BASE_URL}/api/auth/login-methods/`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
        withCredentials: true,
      }
    );

    if (response.data && response.data.status === 'success') {
      console.log('✅ [AUTH-SERVICE] Métodos de login obtidos:', response.data.login_method);
      return response.data;
    }

    return null;
  } catch (error) {
    console.error('❌ [AUTH-SERVICE] Erro ao obter métodos de login:', error);
    return null;
  }
}

/**
 * 🔐 Define password para utilizadores que só têm Google
 */
export async function setPassword(
  password: string,
  confirmPassword: string
): Promise<SetPasswordResponse | null> {
  try {
    console.log('🔐 [AUTH-SERVICE] Definindo password...');

    const token = getAccessToken();
    if (!token) {
      throw new Error('Sem token de acesso');
    }

    const response = await axios.post<SetPasswordResponse>(
      `${API_CONFIG.BASE_URL}/api/auth/set-password/`,
      {
        password,
        confirm_password: confirmPassword
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
        withCredentials: true,
      }
    );

    if (response.data && response.data.status === 'success') {
      console.log('✅ [AUTH-SERVICE] Password definida com sucesso');

      // Atualizar tokens se retornados
      if (response.data.access) {
        localStorage.setItem('accessToken', response.data.access);
      }
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh);
      }

      return response.data;
    }

    return null;
  } catch (error) {
    console.error('❌ [AUTH-SERVICE] Erro ao definir password:', error);

    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    throw error;
  }
}

/**
 * 🔗 Linka conta Google ao utilizador atual
 */
export async function linkGoogleAccount(
  googleId: string,
  googleEmail: string,
  password: string
): Promise<LinkGoogleResponse | null> {
  try {
    console.log('🔗 [AUTH-SERVICE] Linkando conta Google...');

    const token = getAccessToken();
    if (!token) {
      throw new Error('Sem token de acesso');
    }

    const response = await axios.post<LinkGoogleResponse>(
      `${API_CONFIG.BASE_URL}/api/auth/link-google/`,
      {
        google_id: googleId,
        google_email: googleEmail,
        password
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
        withCredentials: true,
      }
    );

    if (response.data && response.data.status === 'success') {
      console.log('✅ [AUTH-SERVICE] Google linkado com sucesso');
      return response.data;
    }

    return null;
  } catch (error) {
    console.error('❌ [AUTH-SERVICE] Erro ao linkar Google:', error);

    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    throw error;
  }
}

/**
 * ❌ Remove ligação Google da conta
 */
export async function unlinkGoogleAccount(
  password: string
): Promise<LinkGoogleResponse | null> {
  try {
    console.log('❌ [AUTH-SERVICE] Removendo ligação Google...');

    const token = getAccessToken();
    if (!token) {
      throw new Error('Sem token de acesso');
    }

    const response = await axios.post<LinkGoogleResponse>(
      `${API_CONFIG.BASE_URL}/api/auth/unlink-google/`,
      { password },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
        withCredentials: true,
      }
    );

    if (response.data && response.data.status === 'success') {
      console.log('✅ [AUTH-SERVICE] Google removido com sucesso');
      return response.data;
    }

    return null;
  } catch (error) {
    console.error('❌ [AUTH-SERVICE] Erro ao remover Google:', error);

    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    throw error;
  }
}

/**
 * 🔄 Altera a password do utilizador
 */
export async function changePassword(
  newPassword: string
): Promise<{ status: string; message: string; access?: string; refresh?: string } | null> {
  try {
    console.log('🔄 [AUTH-SERVICE] Alterando password...');

    const token = getAccessToken();
    if (!token) {
      throw new Error('Sem token de acesso');
    }

    const response = await axios.post(
      `${API_CONFIG.BASE_URL}/backend/users/change-password/`,
      {
        new_password: newPassword
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
        withCredentials: true,
      }
    );

    if (response.data) {
      console.log('✅ [AUTH-SERVICE] Password alterada com sucesso');

      // Update tokens if returned (keeps user logged in)
      if (response.data.access) {
        localStorage.setItem('accessToken', response.data.access);
        console.log('🔑 [AUTH-SERVICE] Access token atualizado');
      }
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh);
        console.log('🔑 [AUTH-SERVICE] Refresh token atualizado');
      }

      return response.data;
    }

    return null;
  } catch (error) {
    console.error('❌ [AUTH-SERVICE] Erro ao alterar password:', error);

    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }

    throw error;
  }
}

/**
 * 🔒 Valida força da password localmente
 */
export function validatePasswordStrength(password: string): {
  valid: boolean;
  strength: 'weak' | 'medium' | 'strong';
  errors: string[];
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
} {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password),
  };

  const errors: string[] = [];

  if (!checks.length) errors.push('Mínimo 8 caracteres');
  if (!checks.uppercase) errors.push('Pelo menos uma letra maiúscula');
  if (!checks.lowercase) errors.push('Pelo menos uma letra minúscula');
  if (!checks.number) errors.push('Pelo menos um número');
  if (!checks.special) errors.push('Pelo menos um caractere especial (!@#$%...)');

  const passedChecks = Object.values(checks).filter(Boolean).length;

  let strength: 'weak' | 'medium' | 'strong' = 'weak';
  if (passedChecks === 5 && password.length >= 12) {
    strength = 'strong';
  } else if (passedChecks >= 4) {
    strength = 'medium';
  }

  return {
    valid: errors.length === 0,
    strength,
    errors,
    checks
  };
}
